import { useCallback, useState } from "react";

// Temporary storage key for onboarding state
const ONBOARDING_STORAGE_KEY = "onboarding_state";

// Max age for stored state (1 day in milliseconds)
const MAX_STATE_AGE_MS = 24 * 60 * 60 * 1000;

// Available presets (same as step-review.tsx)
export const DEFAULT_PACKS = [
  "professional_natural",
  "professional_black",
  "professional_in_big_city",
  "outdoor_adventure",
  "keynote_speaker",
  "professional_real_estate",
  "nature",
  "glamour",
];

export const onboardingPackName: Record<string, string> = {
  professional_natural: "Natural",
  professional_black: "Black",
  professional_in_big_city: "Large City",
  outdoor_adventure: "Outdoor",
  keynote_speaker: "Keynote Speaker",
  professional_real_estate: "Real Estate",
  nature: "Nature",
  glamour: "Glamour",
};

export const packPrompts: Record<string, string> = {
  professional_natural:
    "Without changing the face features. Change the background to a blurred modern office with natural lighting. Change the clothes to professional business attire. Every elements should seamlessly merge into a realistic, professional headshot. Adjust lighting for a side natural lighting. Shallow depth of view.",

  professional_black:
    "Without changing the face features. Change the background to a black wall. Change the clothes to professional business attire. Every elements should seamlessly merge into a realistic, professional headshot. Adjust lighting for a side natural lighting. Shallow depth of view.",

  professional_in_big_city:
    "Without changing the face features. Change the background to a blurred city business center with natural outdoor lighting. Change the clothes to professional business attire. Every elements should seamlessly merge into a realistic, professional headshot. Adjust lighting for a side natural lighting. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",

  outdoor_adventure:
    "Without changing the face features. Change the background to a natural outdoor environment with bright natural lighting. Change the clothes to casual outdoor wear with a clean and adventurous style. Every elements should seamlessly merge into a realistic, professional headshot. Adjust lighting for a outdoor natural light. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",

  keynote_speaker:
    "Without changing the face features. Change the background to a keynote stage with a blurred presentation in the background. Change the clothes to professional business attire suitable for public speaking. The subject have a microphone in hand. Every elements should seamlessly merge into a realistic, professional headshot with a confident stage presence. Adjust lighting for a dark interior stage highlights. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",

  professional_real_estate:
    "Without changing the face features. Change the background to a blurred modern residential or property setting with natural outdoor lighting, including a softly blurred 'For Sale' or real-estate style signage in the distance. Change the clothes to professional business attire suitable for real-estate work. Every elements should seamlessly merge into a realistic, professional headshot with an approachable and trustworthy atmosphere. Adjust lighting for a side natural lighting. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",

  nature:
    "Without changing the face features. Change the background to a park, golden hours. Change the clothes to relaxed, casual outdoor wear with a calm and approachable style. Every elements should seamlessly merge into a realistic, professional headshot with a peaceful atmosphere. Adjust lighting for a side natural lighting. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",

  glamour:
    "Without changing the face features. Change the background to an elegant, softly lit indoor setting with a warm, inviting atmosphere. Change the clothes to stylish, refined evening wear that conveys confidence and sophistication. Add a subtle, graceful pose suited for a tasteful glamour portrait. Every elements should seamlessly merge into a realistic, elegant headshot. Adjust lighting for a side natural lighting mixed with soft ambient glow. Shallow depth of view. Make sure hair and clothes are perfectly seamlessly integrated into the scene.",
};

export type OnboardingAction = "professional" | "edit" | "train";

export interface OnboardingState {
  imageDataUrl: string | null;
  selectedAction: OnboardingAction | null;
  selectedPack: string | null;
  timestamp: number;
}

const getDefaultState = (): OnboardingState => ({
  imageDataUrl: null,
  selectedAction: null,
  selectedPack: null,
  timestamp: 0,
});

// Save onboarding state to sessionStorage
export const saveOnboardingState = (state: Partial<OnboardingState>) => {
  const existing = getOnboardingState();
  const updated = {
    ...existing,
    ...state,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(updated));
};

// Get onboarding state from sessionStorage
export const getOnboardingState = (): OnboardingState => {
  try {
    const stored = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to parse onboarding state:", e);
  }
  return getDefaultState();
};

// Clear onboarding state
export const clearOnboardingState = () => {
  sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
};

// Check if the stored state is valid and not expired
export const isOnboardingStateValid = (): boolean => {
  const state = getOnboardingState();

  // Check if we have a timestamp and it's not older than MAX_STATE_AGE_MS
  if (!state.timestamp || Date.now() - state.timestamp > MAX_STATE_AGE_MS) {
    return false;
  }

  // Check if we have a selected action
  if (!state.selectedAction) {
    return false;
  }

  // Check if we have a valid image
  if (!state.imageDataUrl || !state.imageDataUrl.startsWith("data:image/")) {
    return false;
  }

  return true;
};

// Custom hook for managing onboarding state
export const useOnboarding = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<string>(DEFAULT_PACKS[0]);
  const [selectedAction, setSelectedAction] = useState<OnboardingAction | null>(
    null
  );

  // Handle file selection
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setUploadedImage(dataUrl);
      // Save to session storage
      saveOnboardingState({ imageDataUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  }, []);

  // Reset to upload state
  const handleReset = useCallback(() => {
    setUploadedImage(null);
    setSelectedAction(null);
    clearOnboardingState();
  }, []);

  // Save action selection
  const saveAction = useCallback(
    (action: OnboardingAction) => {
      setSelectedAction(action);
      saveOnboardingState({
        selectedAction: action,
        selectedPack: action === "professional" ? selectedPack : null,
      });
    },
    [selectedPack]
  );

  // Update selected pack
  const updateSelectedPack = useCallback((pack: string) => {
    setSelectedPack(pack);
    saveOnboardingState({ selectedPack: pack });
  }, []);

  return {
    uploadedImage,
    selectedPack,
    selectedAction,
    handleFiles,
    handleReset,
    saveAction,
    updateSelectedPack,
  };
};
